import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Container, Typography, Card, CardContent, Button, TextField,
  CircularProgress, Alert, Chip, Avatar, Divider, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Collapse,
} from '@mui/material';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import AddIcon from '@mui/icons-material/Add';
import ReplyIcon from '@mui/icons-material/Reply';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { listQAThreads, createQAThread, replyToThread, deleteThread } from '../services/api';

const QAForum = () => {
  const { startupId } = useParams();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newBody, setNewBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [expandedThread, setExpandedThread] = useState(null);
  const [replyText, setReplyText] = useState({});

  const fetchThreads = useCallback(async () => {
    try {
      setLoading(true);
      const res = await listQAThreads(startupId);
      setThreads(res.data?.threads || res.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load Q&A');
    } finally {
      setLoading(false);
    }
  }, [startupId]);

  useEffect(() => { fetchThreads(); }, [fetchThreads]);

  const handleCreateThread = async () => {
    if (!newTitle.trim()) return;
    setSubmitting(true);
    try {
      await createQAThread(startupId, { title: newTitle, body: newBody });
      setNewTitle('');
      setNewBody('');
      setNewDialogOpen(false);
      await fetchThreads();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create thread');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = async (threadId) => {
    const text = replyText[threadId];
    if (!text?.trim()) return;
    try {
      await replyToThread(threadId, { body: text });
      setReplyText((prev) => ({ ...prev, [threadId]: '' }));
      await fetchThreads();
    } catch (err) {
      setError('Failed to post reply');
    }
  };

  const handleDelete = async (threadId) => {
    if (!window.confirm('Delete this thread?')) return;
    try {
      await deleteThread(threadId);
      await fetchThreads();
    } catch (err) {
      setError('Failed to delete thread');
    }
  };

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>
  );

  return (
    <Box sx={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <Box sx={{ background: 'linear-gradient(135deg, #052d50 0%, #478289 100%)', color: 'white', py: 6 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>Q&A Forum</Typography>
              <Typography variant="h6" sx={{ opacity: 0.85, mt: 1 }}>
                Ask questions and get answers about this opportunity
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setNewDialogOpen(true)}
              sx={{ backgroundColor: 'white', color: '#052d50', '&:hover': { backgroundColor: '#e0e0e0' } }}
            >
              New Question
            </Button>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

        {threads.length === 0 ? (
          <Card sx={{ textAlign: 'center', py: 8 }}>
            <QuestionAnswerIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
            <Typography variant="h6" color="textSecondary">No questions yet</Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Be the first to ask a question about this startup.
            </Typography>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setNewDialogOpen(true)}>
              Ask a Question
            </Button>
          </Card>
        ) : (
          threads.map((thread) => (
            <Card key={thread.id} sx={{ mb: 2, '&:hover': { boxShadow: 3 } }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, cursor: 'pointer' }}
                      onClick={() => setExpandedThread(expandedThread === thread.id ? null : thread.id)}>
                      {thread.title}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Avatar sx={{ width: 24, height: 24, fontSize: 12, bgcolor: '#478289' }}>
                        {(thread.author?.name || 'U')[0]}
                      </Avatar>
                      <Typography variant="caption" color="textSecondary">
                        {thread.author?.name || 'Anonymous'} · {new Date(thread.createdAt).toLocaleDateString()}
                      </Typography>
                      <Chip label={`${thread.replies?.length || 0} replies`} size="small" variant="outlined" />
                    </Box>
                  </Box>
                  <Box>
                    <IconButton size="small" onClick={() => handleDelete(thread.id)}><DeleteIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => setExpandedThread(expandedThread === thread.id ? null : thread.id)}>
                      {expandedThread === thread.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>
                </Box>

                <Collapse in={expandedThread === thread.id}>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>{thread.body}</Typography>
                    <Divider sx={{ my: 2 }} />

                    {thread.replies?.map((reply, i) => (
                      <Box key={reply.id || i} sx={{ ml: 3, mb: 2, pl: 2, borderLeft: '3px solid #e0e0e0' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Avatar sx={{ width: 20, height: 20, fontSize: 10, bgcolor: '#052d50' }}>
                            {(reply.author?.name || 'U')[0]}
                          </Avatar>
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            {reply.author?.name || 'Anonymous'}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {new Date(reply.createdAt).toLocaleDateString()}
                          </Typography>
                        </Box>
                        <Typography variant="body2">{reply.body}</Typography>
                      </Box>
                    ))}

                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                      <TextField
                        fullWidth size="small" placeholder="Write a reply..."
                        value={replyText[thread.id] || ''}
                        onChange={(e) => setReplyText((prev) => ({ ...prev, [thread.id]: e.target.value }))}
                        onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleReply(thread.id)}
                      />
                      <Button variant="contained" startIcon={<ReplyIcon />}
                        onClick={() => handleReply(thread.id)}
                        sx={{ backgroundColor: '#478289', whiteSpace: 'nowrap' }}>
                        Reply
                      </Button>
                    </Box>
                  </Box>
                </Collapse>
              </CardContent>
            </Card>
          ))
        )}
      </Container>

      <Dialog open={newDialogOpen} onClose={() => setNewDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Ask a Question</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Question Title" value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)} sx={{ mt: 1, mb: 2 }} />
          <TextField fullWidth label="Details (optional)" value={newBody}
            onChange={(e) => setNewBody(e.target.value)} multiline rows={4} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateThread} disabled={submitting || !newTitle.trim()}
            sx={{ backgroundColor: '#478289' }}>
            {submitting ? <CircularProgress size={20} /> : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QAForum;
